import { Ng2StateDeclaration, Transition } from '@uirouter/angular';
import { EdlPublicComponent } from './layout/public/edl-public.component';
import { EdlAuthenticatedNavigationComponent } from './layout/authenticated/edl-authenticated-navigation.component';
import {EdlPrintComponent} from './layout/print/edl-print.component';
import { AccessRoleHelper } from './model/helpers/access-role.helper';
import { PortalHelpersService } from './services/portal-helpers/portal-helpers.service';
import { SessionService, UserService, PageNotFoundComponent, UnauthorizedComponent, GenericErrorPageComponent, EdlSiteMapPageComponent, DEFAULT_PORTAL_URL_SUBDIRECTORY } from '@edl-development/edl-common';
import { AuthenticatedHeaderComponent } from './layout/authenticated/authenticated-header/authenticated-header.component';
import { AuthenticatedComponent } from './layout/authenticated/authenticated/authenticated.component';
import { AdministrationLazyLoadedStates } from './lazy-loaded-states/administration.lazy-loaded-states';
import { firstValueFrom } from 'rxjs';
import { AllUsersProgressLazyLoadedStates } from './lazy-loaded-states/all-users-progress.lazy-loaded-states';
import { ComplianceItemsLazyLoadedStates } from './lazy-loaded-states/compliance-items.lazy-loaded-states';

export function onEnterAuthenticated(trans: Transition) {
    var sessionService: SessionService = trans.injector().get(SessionService);

    if (!sessionService.isAuthenticated()) {
        sessionService.redirectState = {
            name: trans.$to().name,
            params: trans.params()
        };

        return trans.router.stateService.target('public.login');
    }
}

export function onEnterDashboard(trans: Transition) {
    var sessionService: SessionService = trans.injector().get(SessionService);
    var portalHelpersService: PortalHelpersService = trans.injector().get(PortalHelpersService);

    if (!sessionService.isAuthenticated()) {
        return trans.router.stateService.target('public.login');
    }     else   if (sessionService.redirectState) {
        let redirect: { name: string, params: any } = sessionService.redirectState;
        sessionService.redirectState = null;
        return trans.router.stateService.target(redirect.name, redirect.params, {reload: true});
    } else {

        const loggedInPortalUrl = trans.params()?.loggedInPortalUrl || DEFAULT_PORTAL_URL_SUBDIRECTORY;

        const portalSettings = sessionService.user.PortalSettings;
        const isAct48HoursOnly = portalHelpersService.isAct48HoursOnly(portalSettings);

        if (isAct48HoursOnly) {
            if (AccessRoleHelper.isLimitedSupervisor(sessionService.user.AccessRoleID)) {
                return trans.router.stateService.target('authenticated.requests.sm.search', { type:'myrequests', page: 1, loggedInPortalUrl }, {reload: true});
            } else {
                return trans.router.stateService.target('authenticated.my-items', { loggedInPortalUrl }, {reload: true});
            }
        } else {
            return trans.router.stateService.target('authenticated.my-items', { loggedInPortalUrl }, {reload: true});
        }

    }
}

export function onEnterLogout(trans: Transition) {
    const sessionService: SessionService = trans.injector().get(SessionService);

    // Need to include any custom options that were passed to the transition
    const custom = trans?.options()?.custom;

    // Destroy the session
    sessionService.destroy();

    // Redirect to the login page
    return trans.router.stateService.target('public.login', {}, { custom });
}

export function onEnterSwitchPortals(transition: Transition) {
    const sessionService: SessionService = transition.injector().get(SessionService);

    const loggedInPortalUrl = sessionService?.PortalSettings?.URLSubdirectory || DEFAULT_PORTAL_URL_SUBDIRECTORY;

    return transition.router.stateService.target('authenticated.dashboard', { loggedInPortalUrl });
}

export function resolveSwitchPortals(transition: Transition, sessionService: SessionService, userService: UserService) {
    const params = transition.params();
    const portalId: number = params.portalId;

    const currentPortalId = sessionService?.PortalSettings?.PortalID;

    // Don't need to switch portals if we're already in the correct portal
    if (portalId == currentPortalId) {
        return Promise.resolve();
    }

    return firstValueFrom(userService.switchPortal(portalId));
}

export const EdlComplyStates: Ng2StateDeclaration[] = [{
    name: 'public',
    abstract: true,
    data: {
        isPublic: true
    },
    views: {
        'base': {
            component: EdlPublicComponent
        }
    }
}, {
    name: 'public.page-not-found',
    url: '/404',
    views: {
        'content@public': {
            component: PageNotFoundComponent
        }
    }
}, {
    name: 'public.unauthorized',
    url: '/401',
    views: {
        'content@public': {
            component: UnauthorizedComponent
        }
    }
}, {
    name: 'public.generic-error',
    url: '/500',
    views: {
        'content@public': {
            component: GenericErrorPageComponent
        }
    }
}, {
    name: 'public.logout',
    onEnter: onEnterLogout
}, {
    name: 'authenticated',
    abstract: true,
    url: '/p/{loggedInPortalUrl:[a-zA-Z0-9]+}',
    views: {
        'base': {
            component: AuthenticatedComponent
        },
        'header@authenticated': {
            component: AuthenticatedHeaderComponent
        },
        'navigation@authenticated': {
            component: EdlAuthenticatedNavigationComponent
        }
    },
    params: {
        loggedInPortalUrl: {
            value: DEFAULT_PORTAL_URL_SUBDIRECTORY
        }
    },
    onEnter: onEnterAuthenticated
},
{
    name: 'authenticated.dashboard',
    url: '/',
    onEnter: onEnterDashboard
}, {
    name: 'authenticated.site-map',
    url: '/site-map',
    views: {
        'content@authenticated': {
            component: EdlSiteMapPageComponent
        }
    }
}, {
    name: 'authenticated.switch-portals',
    url: '/switch-portals/:portalId',
    onEnter: onEnterSwitchPortals,
    resolve: [{
        token: 'portalData',
        deps: [Transition, SessionService, UserService],
        resolveFn: resolveSwitchPortals
    }]
}, {
    name: 'print',
    abstract: true,
    url: '/print',
    views: {
        'base': {
            component: EdlPrintComponent
        }
    }
},

{
  name: 'public.user-agreement.**',
  url: '/user-agreement',
  loadChildren: () => import('./user-agreement/user-agreement.module').then(mod => mod.UserAgreementModule)
},

// FUTURE STATES
...AdministrationLazyLoadedStates.generateLazyLoadedStates(),
{
    name: 'authenticated.reports.**',
    url: '/reporting-management',
    loadChildren: () => import('./reports/reports.module').then(mod => mod.ReportsModule)
}, {
    name: 'authenticated.groups.**',
    url: '/groups',
    loadChildren: () => import('./groups/pages/groups.module').then(mod => mod.GroupsModule)
}, {
    name: 'authenticated.volunteerAreas.**',
    url: '/volunteerAreas',
    loadChildren: () => import('./groups/pages/groups.module').then(mod => mod.GroupsModule)
}, {
    name: 'authenticated.users.**',
    url: '/users',
    loadChildren: () => import('./users/users.module').then(mod => mod.UsersModule)
}, {
    name: 'authenticated.questionnaires.**',
    url: '/questionnaires',
    loadChildren: () => import('./questionnaires/questionnaires.module').then(mod => mod.QuestionnairesModule)
}, {
    name: "authenticated.requests.**",
    url: "/requests",
    loadChildren: () => import('./requests/requests.module').then(mod => mod.RequestsModule)
},
...AllUsersProgressLazyLoadedStates.generateLazyLoadedStates(),
...ComplianceItemsLazyLoadedStates.generateLazyLoadedStates(),
{
    name: 'authenticated.messages.**',
    url: '/messages',
    loadChildren: () => import('./messages/messages.module').then(mod => mod.MessagesModule)
},
{
    name: 'authenticated.my-items.**',
    url: '/my-items',
    loadChildren: () => import('./all-users-progress/states/my-items/my-items.module').then(mod => mod.MyItemsModule)
}];
